import { useState } from "react";
import { Star, X } from "lucide-react";
import type { LeadDetail } from "@school-sales-ai/shared";
import { useCatalogContext } from "@/hooks/useCatalog";
import { useCatalogNoun } from "@/hooks/useCatalogNoun";
import {
  useAddLeadInterest,
  usePromoteLeadInterest,
  useRemoveLeadInterest,
} from "@/hooks/useLeadInterests";

/**
 * The lead's programs of interest, with the primary one marked and swappable.
 *
 * Takes the whole `lead` rather than a `leadId` on purpose: the list already
 * arrives inside the lead-detail payload, so this component runs no query and
 * costs no extra request. (`LeadVisitSection` self-fetches because its data is
 * not in that payload — this one must not copy that.)
 *
 * Renders in both the desktop rail and the mobile Perfil tab.
 *
 * ## Two modes, because a program can belong to a person
 *
 * With no candidates — the majority, and every lead before that feature — this
 * is exactly what it always was: the full list, primary included.
 *
 * With candidates, `LeadCandidatesSection` above already shows each person's
 * program on their own card, so repeating it here states the same fact twice.
 * The section then narrows to the programs NO candidate claims, says so in its
 * header, and hides entirely when there are none.
 *
 * The primary control goes away in that mode too, and that is the point rather
 * than a side effect: between two children the choice is arbitrary, so offering
 * a star implies a decision that does not exist. `leads.product_id` keeps its
 * value underneath — the board still groups by it — it just stops being
 * presented as something to pick.
 *
 * Nothing is hidden from the DATA: `lead_product_interests` remains the
 * canonical set every reader uses (reports, digest, the prompt, the board).
 * This is a rendering decision only.
 */
export function LeadInterestsSection({ lead }: { lead: LeadDetail }): React.JSX.Element | null {
  const [confirmingRemoveId, setConfirmingRemoveId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const { data: catalog } = useCatalogContext();
  const noun = useCatalogNoun();

  const add = useAddLeadInterest(lead.id);
  const promote = usePromoteLeadInterest(lead.id);
  const remove = useRemoveLeadInterest(lead.id);

  const allInterests = lead.interests ?? [];
  const candidates = lead.candidates ?? [];
  const hasCandidates = candidates.length > 0;

  // Matched on productId, not on the interest row: two siblings at the same
  // grade are two people sharing one interest.
  const claimedProductIds = new Set(
    candidates.map((c) => c.productId).filter((id): id is string => id !== null),
  );
  const interests = hasCandidates
    ? allInterests.filter((i) => !claimedProductIds.has(i.productId))
    : allInterests;

  const products = catalog?.products ?? [];
  // Filtered against the FULL set, not the visible one: otherwise the picker
  // would offer a program a candidate already holds and the server would reject
  // it as a duplicate.
  const takenIds = new Set(allInterests.map((i) => i.productId));
  const addable = products.filter((p) => !takenIds.has(p.id));

  // Every program has an owner, so there is nothing left to say. An empty
  // "sin asignar" box would be noise on the common family case.
  if (hasCandidates && interests.length === 0) return null;

  // Hidden entirely when there is nothing to show AND nothing to add, so a
  // tenant with no catalog never sees an empty box.
  if (interests.length === 0 && addable.length === 0) return null;

  const busy = add.isPending || promote.isPending || remove.isPending;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-ink-300">
          {hasCandidates ? "Sin asignar" : `${noun.Plural} de interés`}
        </p>
        {addable.length > 0 && !adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="rounded-lg border border-dashed border-ink-300 px-2 py-0.5 text-[11px] font-medium text-ink-500 hover:bg-ink-050"
          >
            + Agregar
          </button>
        )}
      </div>

      {interests.length === 0 && (
        <p className="text-xs text-ink-300">Sin {noun.plural} registrados aún.</p>
      )}

      <div className="flex flex-col gap-2">
        {interests.map((interest) => (
          <div
            key={interest.id}
            className="flex flex-col gap-1.5 rounded-[12px] border border-ink-100 bg-white p-2.5"
          >
            <div className="flex items-start justify-between gap-2">
              <span className="min-w-0 break-words text-xs font-medium text-ink-900">
                {interest.productName}
              </span>
              <button
                type="button"
                aria-label={`Quitar ${interest.productName}`}
                disabled={busy}
                onClick={() =>
                  confirmingRemoveId === interest.id
                    ? remove.mutate(interest.id, {
                        onSettled: () => setConfirmingRemoveId(null),
                      })
                    : setConfirmingRemoveId(interest.id)
                }
                className="shrink-0 rounded p-0.5 text-ink-300 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            {confirmingRemoveId === interest.id && (
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-red-600">¿Quitar este {noun.singular}?</span>
                <button
                  type="button"
                  onClick={() => setConfirmingRemoveId(null)}
                  className="text-[11px] font-medium text-ink-500 underline"
                >
                  Cancelar
                </button>
              </div>
            )}

            {/* The whole row disappears with candidates present unless the
                archived badge still has something to say. */}
            {(!hasCandidates || interest.isArchived) && (
              <div className="flex flex-wrap items-center gap-1.5">
                {!hasCandidates && !interest.isPrimary && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => promote.mutate(interest.id)}
                    className="rounded-full border border-ink-200 px-2 py-0.5 text-[10px] font-medium text-ink-500 hover:bg-ink-050 disabled:opacity-40"
                  >
                    Hacer principal
                  </button>
                )}
                {!hasCandidates && interest.isPrimary && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-ink-100 px-2 py-0.5 text-[10px] font-semibold text-ink-500">
                    <Star className="h-2.5 w-2.5 fill-current" />
                    Principal
                  </span>
                )}
                {/* The name comes from the API join, never from the catalog
                    context — that lists active products only, so an archived
                    interest would otherwise render blank. */}
                {interest.isArchived && (
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                    Archivado
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {adding && (
        <select
          autoFocus
          disabled={busy}
          defaultValue=""
          onChange={(e) => {
            const productId = e.target.value;
            if (!productId) return;
            add.mutate(productId, { onSettled: () => setAdding(false) });
          }}
          onBlur={() => setAdding(false)}
          className="w-full rounded-lg border border-ink-200 px-2 py-1.5 text-xs text-ink-900"
        >
          <option value="" disabled>
            Elige un {noun.singular}…
          </option>
          {addable.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
